<template>
    <DashboardLayout>
        <Breadcrumb class="flex breadcrumb" :model="items" />
        <div class="card flex flex-column justify-center pt-4 px-4" style="min-height: calc(100vh - 100px);">
            <div v-if="viewStack.length > 1" class="mb-4">
                <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer "
                    @click="goBack" />
            </div>
            <div v-if="currentView.type === 'list' && Tutors.length">
                <div class="flex align-items-end justify-content-end gap-2">
                    <Button label="Add a Tutor" class="yellow-button-small" @click="createNewTutor" />
                    <Button label="Pending Invitations" class="yellow-button-small" @click="goPendingInvites" />
                </div>
                <div class="tutor-grid">
                    <SuspendedMemberOverlay v-for="tutor in Tutors" :key="tutor.id"
                        :suspended="!!tutor.instituteAccessSuspended" member-type="tutor">
                        <div class="tutor-card" @click="viewTutorProfile(tutor)">
                            <div class="tutor-image-container">
                                <img :src="imageUrls[tutor.id] || blankProfile" alt="Tutor Profile" class="tutor-image" />
                            </div>
                            <div class="tutor-info">
                                <span class="Raleway font-semibold tutor-name">{{ tutor.username }}</span>
                                <span class="tutor-details">
                                    {{ cleanupLables(tutor.tutorType) }} - {{ getLastWord(tutor.address) || 'N/A' }}
                                </span>
                                <div class="flex align-items-center gap-2">
                                    <span class="Paragraph-Medium">{{ tutor.averageRating || "0" }}</span>
                                    <span class="Caption">({{ tutor.reviewCount || "0" }} reviews)</span>
                                </div>
                            </div>
                        </div>
                    </SuspendedMemberOverlay>
                </div>
            </div>
            <div v-else-if="currentView.type === 'profile'">
                <TutorProfile :inner-map="true" :tutor-id="profile" />
            </div>
            <div v-else-if="currentView.type === 'list' && Tutors.length === 0" class="flex flex-column h-full">
                <div class="flex align-items-end justify-content-end gap-2">
                    <Button label="Add a Tutor" class="yellow-button-small" @click="createNewTutor" />
                    <Button label="Pending Invitations" class="yellow-button-small" @click="goPendingInvites" />
                </div>
                <div class="flex flex-column align-items-center justify-content-center w-full h-full">
                    <span class="Caption text-gray-700 text-center mb-4">You don’t have any Enrolled tutors yet,
                        please schedule a class with a tutor to get enrolled.</span>
                </div>
            </div>
            <div v-else class="flex align-items-center justify-content-center h-full">
                <div class="flex flex-column ">
                    <span class="Caption text-gray-700 text-center"> You don’t have any Enrolled tutors yet,
                        please schedule a class with a tutor to get enrolled</span>
                </div>
            </div>

            <!-- Disclaimer -->
            <div v-if="currentView.type === 'list'" class="text-center w-full max-w-xl mx-auto py-4 px-6 mt-auto pt-6">

                <h3 class="text-lg font-semibold mb-2" style="color: #8B5E00;">
                    Disclaimer
                </h3>

                <p class="text-700 m-0 mb-3" style="font-size: 0.8125rem;">
                    ⚠️ <strong>Important Notice:</strong> All classes, communications, and payments must remain on the ILearn'd Tutors platform. Institutes are responsible for their tutors and students. AI features are provided for educational support only, and academic outcomes cannot be guaranteed.
                </p>

                <p class="text-700 m-0" style="font-size: 0.8125rem;">
                    📢 <strong>Platform Updates:</strong> The Institute Portal is continuously evolving, and new features, tools, and improvements will be released regularly to enhance your experience.
                </p>

            </div>
        </div>

        <Dialog v-model:visible="displayDialog" header="Register New Tutor" :modal="true"
            class="p-fluid w-11 md:w-6 lg:w-4 xl:w-3 mx-3">

            <div class="field grid align-items-center gap-2 mb-3">
                <label for="username" class="col-12 md:col-3 Label">Username</label>
                <div class="col-12">
                    <InputText id="username" v-model="newTutor.username" required size="small" variant="filled"
                        class="w-full" />
                </div>
            </div>

            <div class="field grid align-items-center gap-2 mb-3">
                <label for="email" class="col-12 md:col-3 Label">Email</label>
                <div class="col-12 ">
                    <InputText id="email" type="email" v-model="newTutor.email" required size="small" variant="filled"
                        class="w-full" />
                </div>
            </div>

            <!-- <div class="field grid align-items-center gap-2 mb-3">
                <label for="password" class="col-12 md:col-3 Label">Password</label>
                <div class="col-12 ">
                    <InputText id="password" type="password" v-model="newTutor.password" required size="small"
                        variant="filled" class="w-full" />
                </div>
            </div> -->

            <template #footer>
                <div class="flex flex-column md:flex-row justify-content-end gap-2 w-full">
                    <Button label="Cancel" class="yellow-button-small w-full md:w-auto"
                        @click="displayDialog = false" />
                    <Button label="Create Tutor" class="yellow-button-small w-full md:w-auto"
                        @click="handleTutorCreation" :loading="loading" />
                </div>
            </template>
        </Dialog>

    </DashboardLayout>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import Breadcrumb from 'primevue/breadcrumb';
import Button from "primevue/button";
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { getTutors, createTutor } from "@/api/institutes";
import { useAuthStore } from "@/stores/authStore";
import { generateSignedUrl } from "@/api/storage";
import blankProfile from '@/assets/blank-profile.png';
import TutorProfile from "@/views/publicProfile/tutorProfile.vue";
import SuspendedMemberOverlay from "./SuspendedMemberOverlay.vue";
import { mixpanelService } from "@/services/mixpanel";
import { cleanupLables } from "@/utils/cleanups";
import { notifyError, notifyWarn } from "@/utils/notification";
import { useRouter } from "vue-router";

const router = useRouter();
const goPendingInvites = () => {
  router.push("/pendingInvitations");
};
const authStore = useAuthStore();
const Tutors = ref([]);
const profile = ref(null);
const viewStack = ref([
    { type: "list", label: "Our Tutors", data: null }
]);
const items = computed(() => {
    return [
        { label: "Dashboard" },
        ...viewStack.value.map(v => ({ label: v.label }))
    ];
});
const currentView = computed(() =>
    viewStack.value[viewStack.value.length - 1]
);

const toast = useToast();
const displayDialog = ref(false);
const loading = ref(false);
const keycloakRole = ref(null);
const newTutor = ref({
    username: '',
    email: '',
    // password: '',
    role: 'tutor'
});
const imageUrls = ref({});
function goBack() {
    if (viewStack.value.length > 1) {
        viewStack.value.pop();
    }
}

function createNewTutor() {
    displayDialog.value = true;
}

function viewTutorProfile(tutor) {
    viewStack.value.push({ type: "profile", label: "Tutor Profile", data: tutor });
    console.log(tutor)
    profile.value = tutor._id;
    // const routeData = router.resolve({
    //     path: `/tutorProfile/${tutor._id}`
    // });
    // window.open(routeData.href, '_blank');
}

async function fetchTutorImageUrls(tutors) {
    for (const tutor of tutors) {
        const filePath = tutor.professionalPhotoURL;

        if (!filePath || filePath === 'Service is not available in local environment') {
            imageUrls.value[tutor.id] = blankProfile;
            continue;
        }

        try {
            const url = await generateSignedUrl(filePath);
            imageUrls.value[tutor.id] = url;
        } catch (error) {
            console.error(`Error fetching signed URL for ${tutor.username}:`, error);
            imageUrls.value[tutor.id] = blankProfile;
        }
    }
}

async function fetchTutors() {
    try {
        const response = await getTutors();
        Tutors.value = response.data.data.tutors;
        await fetchTutorImageUrls(Tutors.value);
    } catch (error) {
        console.error("Error fetching tutors:", error.status);
        if (error.status === 404) {
            Tutors.value = [];
        }
    }
}

async function handleTutorCreation() {
    loading.value = true;
    try {
        const payload = {
            username: newTutor.value.username,
            email: newTutor.value.email,
            // password: newTutor.value.password,
            role: keycloakRole.value,
        };

        // Tracking institute tutor creation event with Mixpanel
        const response = await createTutor(payload);
        const userId = authStore.user.id || authStore.user._id || authStore.user.userId;
        const newTutorId = response.data.data.userId;

        mixpanelService.track("Institute Tutor Created", {
            institute_id: userId,
            institute_name: authStore.user.username,

            tutor_id: newTutorId,
            tutor_email: newTutor.value.email,
            tutor_name: newTutor.value.username,

            created_at: new Date().toISOString()
        });

        // Track on new tutor as a new user in Mixpanel
        mixpanelService.trackAsNewUser(
            newTutorId, // User ID
            {           // User Properties
                $email: newTutor.value.email,
                $name: newTutor.value.username,
                role: "tutor",
                $created: new Date().toISOString(),
                registration_completed: false,
                created_by: "institute"
            },
            "User Signed Up", // Event Name
            {                 // Event Properties
                role: "tutor",
                method: "Email",
                created_by: "institute"
            }
        );

        toast.add({
            severity: 'success',
            summary: 'Tutor Created',
            detail: `Tutor **${newTutor.value.username}** created. Share the login credentials with your tutor to complete the registration and background verification.`,
            life: 8000
        });

        displayDialog.value = false;
        newTutor.value = { username: '', email: '', /* password: '', */ role: 'tutor' };

        await fetchTutors();

    } catch (error) {
        if (error.response?.data?.code === 403) {
            notifyWarn("Plan Limit Reached", error.response.data.message);
        } else {
            notifyError(error.response?.data?.status || "Error", error.response?.data?.message || "Could not create tutor. Please try again.");
        }
    } finally {
        loading.value = false;
    }
}

const fetchKeycloakRoles = async () => {
    keycloakRole.value = await authStore.findKeycloackRole('tutor');
}
const getLastWord = (address) => {
    if (!address) return "";
    const words = address.trim().split(/\s+/);
    return words[words.length - 1];
};
onMounted(async () => {
    await fetchTutors();
    await fetchKeycloakRoles();
});
</script>
<style scoped>
.card {
    min-height: calc(100vh - 11rem);
}

.back-button {
    color: var(--gray-600);
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.back-button:active {
    color: var(--gray-600) !important;
}

.navigation {
    display: flex !important;
    flex-wrap: wrap !important;
    display: inline-block;
    padding: 10px;
}

.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

/* New Tutor Card Styles */
.tutor-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
    padding: 20px 0;
}

.tutor-card {
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    border: 1px solid #e0e0e0;
}

.tutor-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.tutor-image-container {
    width: 100%;
    aspect-ratio: 1/1;
    /* Ensure a square image container */
    overflow: hidden;
    border-radius: 4px;
}

.tutor-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.tutor-info {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.tutor-name {
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--text-color);
    /* Assuming PrimeVue variables */
    overflow-wrap: anywhere;
    word-break: break-word;
}

.tutor-details {
    font-size: 0.875rem;
    color: var(--gray-600);
}

.tutor-rating {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-color-secondary);
}

.yellow-button-small {
    white-space: nowrap;
}

@media (max-width: 767px) {
    .breadcrumb {
        font-size: 12px;
    }

    .tutor-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    }
}
</style>